import React, { useState, useEffect } from "react";
import {
  FileText,
  Hash,
  User,
  Calendar,
  Building,
  Activity,
} from "lucide-react";
import { fetchBillHistory, STORAGE_KEYS } from "../../services/api";

// User Type Detection
const getUserTypeFlags = async () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);
      return {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }
  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const BillingHistory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donationRequests, setDonationRequests] = useState([]);
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  useEffect(() => {
    loadBillingHistory();
    loadUserType();
  }, []);

  const loadUserType = async () => {
    try {
      const userTypeFlags = await getUserTypeFlags();
      setUserType(userTypeFlags);
    } catch (err) {
      console.error("Failed to load user type:", err);
    }
  };

  const loadBillingHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchBillHistory();
      if (response && response.records) {
        setDonationRequests(response.records);
      } else {
        setDonationRequests([]);
      }
    } catch (err) {
      setError("Failed to load billing history");
      console.error("Error loading billing history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "N/A";
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "N/A";
    return parseFloat(amount).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading billing history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
            {userType.isHospital ? (
              <FileText className="h-6 w-6 text-red-600 mr-2" />
            ) : userType.isBDO ? (
              <Activity className="h-6 w-6 text-green-600 mr-2" />
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900">
              Billing History
            </h1>
          </div>
          <p className="text-gray-600">
            Patient billing records and invoice information
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing History Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SN
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient Age
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {donationRequests.map((request, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {formatDate(request.DateInvoiced)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Hash className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {request.DocumentNo || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {request.PatientName || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {request.age !== null && request.age !== undefined
                      ? request.age
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-green-500 font-semibold mr-2">
                        NPR
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {formatAmount(request.GrandTotal)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {donationRequests.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">
              No billing records found
            </p>
            <p className="text-sm text-gray-400 mt-2">
              No billing records have been generated yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistory;
